import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { tenantService } from "../services/tenantService";
import {
  TenantValuesInit,
  getTenantCreateVariables,
  getTenantValues,
  TenantValuesType,
  TenantErrorsType,
  tenantDisplayId,
} from "./TenantCommonStore";
import { Tenant } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getTenantTypeValues,
  TenantTypeValuesType,
} from "../../tenantType/store/TenantTypeCommonStore";
import {
  getTenantStatusValues,
  TenantStatusValuesType,
} from "../../tenantStatus/store/TenantStatusCommonStore";
import {
  getTenantPlanValues,
  TenantPlanValuesType,
} from "../../tenantPlan/store/TenantPlanCommonStore";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import {
  getLlmProviderValues,
  LlmProviderValuesType,
} from "../../llmProvider/store/LlmProviderCommonStore";
import { apiUploadFile } from "../../../api/apiClient";
import { uploadFailureReason } from "../../../common/utils";
import { GridChangeSet } from "../../common/types";
import { EsignatureProvider } from "../../../api/graphql";
import { tenantTypeService } from "../../tenantType/services/tenantTypeService";
import { tenantStatusService } from "../../tenantStatus/services/tenantStatusService";
import { tenantPlanService } from "../../tenantPlan/services/tenantPlanService";
import { llmProviderService } from "../../llmProvider/services/llmProviderService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class TenantCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: TenantErrorsType = {};
  service = tenantService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    tenant: { open: true, errors: [] },
    llmLimits: { open: true, errors: [] },
    storageLimits: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: Tenant | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = TenantValuesInit();
  nameError: string | undefined;
  tenantTypeError: string | undefined;
  tenantTypeService = tenantTypeService;
  tenantTypeSearchValue: string = "";
  tenantTypeOptions: TenantTypeValuesType[] = [];
  statusError: string | undefined;
  statusService = tenantStatusService;
  statusSearchValue: string = "";
  statusOptions: TenantStatusValuesType[] = [];
  planError: string | undefined;
  planService = tenantPlanService;
  planSearchValue: string = "";
  planOptions: TenantPlanValuesType[] = [];
  logoError: string | undefined;
  publicUrlError: string | undefined;
  idERPError: string | undefined;
  idCRMError: string | undefined;
  monthlyTokenLimitError: string | undefined;
  currentMonthTokensError: string | undefined;
  tokenLimitResetDayError: string | undefined;
  lastTokenResetDateError: string | undefined;
  ragLlmProviderError: string | undefined;
  ragLlmProviderService = llmProviderService;
  ragLlmProviderSearchValue: string = "";
  ragLlmProviderOptions: LlmProviderValuesType[] = [];
  ragLlmModelError: string | undefined;
  currentStorageBytesError: string | undefined;
  storageByteLimitError: string | undefined;
  eSignatureProviderError: string | undefined;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<TenantValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<TenantValuesType>);
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<TenantValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return tenantDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity

      if (params.callerData?.fieldName === "tenantType") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showTenantType = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.tenantTypeOne({
            id: params.returnData.resultId,
          });
          const tenantType = result.tenantTypeOne;
          this.entityValues.showTenantType = {
            displayId: tenantType.displayId,
            id: tenantType.id,
          };
          this.setTenantTypeOptions([
            getTenantTypeValues(tenantType, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "status") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showStatus = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.tenantStatusOne({
            id: params.returnData.resultId,
          });
          const tenantStatus = result.tenantStatusOne;
          this.entityValues.showStatus = {
            displayId: tenantStatus.displayId,
            id: tenantStatus.id,
          };
          this.setStatusOptions([
            getTenantStatusValues(tenantStatus, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "plan") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showPlan = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.tenantPlanOne({
            id: params.returnData.resultId,
          });
          const tenantPlan = result.tenantPlanOne;
          this.entityValues.showPlan = {
            displayId: tenantPlan.displayId,
            id: tenantPlan.id,
          };
          this.setPlanOptions([
            getTenantPlanValues(tenantPlan, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "ragLlmProvider") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showRagLlmProvider = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.llmProviderOne({
            id: params.returnData.resultId,
          });
          const llmProvider = result.llmProviderOne;
          this.entityValues.showRagLlmProvider = {
            displayId: llmProvider.displayId,
            id: llmProvider.id,
          };
          this.setRagLlmProviderOptions([
            getLlmProviderValues(llmProvider, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupTenantLabel,
        errors: this.getCardErrors("null"),
      },
      {
        id: "tenant",
        label: this.rootStore.langStore.groupTenantTenantLabel,
        errors: this.getCardErrors("tenant"),
      },
      {
        id: "llmLimits",
        label: this.rootStore.langStore.groupTenantLlmLimitsLabel,
        errors: this.getCardErrors("llmLimits"),
      },
      {
        id: "storageLimits",
        label: this.rootStore.langStore.groupTenantStorageLimitsLabel,
        errors: this.getCardErrors("storageLimits"),
      },
    ];
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  setTenantTypeOptions(values: TenantTypeValuesType[]) {
    this.tenantTypeOptions = values;
  }

  *loadTenantTypeValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("TenantType");
    const allOptions = cache.getOptions("TenantType") as TenantTypeValuesType[];

    const searchValue = this.tenantTypeSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setTenantTypeOptions(filtered);
  }

  getTenantTypeOptions() {
    return this.tenantTypeOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setTenantTypeValue(value: string) {
    this.tenantTypeSearchValue = value;
    yield this.loadTenantTypeValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedTenantType(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewTenantType(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "tenantType"
      );
    } else {
      if (value) {
        this.entityValues.showTenantType = value;
        this.entityValues.__tenantType = value.displayId;
      }
    }
  }

  setStatusOptions(values: TenantStatusValuesType[]) {
    this.statusOptions = values;
  }

  *loadStatusValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("TenantStatus");
    const allOptions = cache.getOptions(
      "TenantStatus"
    ) as TenantStatusValuesType[];

    const searchValue = this.statusSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setStatusOptions(filtered);
  }

  getStatusOptions() {
    return this.statusOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setStatusValue(value: string) {
    this.statusSearchValue = value;
    yield this.loadStatusValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedStatus(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewTenantStatus(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "status"
      );
    } else {
      if (value) {
        this.entityValues.showStatus = value;
        this.entityValues.__status = value.displayId;
      }
    }
  }

  setPlanOptions(values: TenantPlanValuesType[]) {
    this.planOptions = values;
  }

  *loadPlanValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("TenantPlan");
    const allOptions = cache.getOptions("TenantPlan") as TenantPlanValuesType[];

    const searchValue = this.planSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setPlanOptions(filtered);
  }

  getPlanOptions() {
    return this.planOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPlanValue(value: string) {
    this.planSearchValue = value;
    yield this.loadPlanValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPlan(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewTenantPlan(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "plan"
      );
    } else {
      if (value) {
        this.entityValues.showPlan = value;
        this.entityValues.__plan = value.displayId;
      }
    }
  }

  logoRestoreFile(id: string) {
    if (this.entityValues.logo.length > 1) {
      alert("No puede haber más de un archivo activo " + id);
      return;
    }
  }

  *logoUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__logo_files];
    const previousName = this.entityValues.logo;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__logo_files.push(newFile);
    this.entityValues.logo = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__logo_files = previousFiles;
      this.entityValues.logo = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("logo");
      } else {
        this.setFieldError(
          "logo",
          this.langStore.TenantLogoLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("logo");
    const fileIndex = this.entityValues.__logo_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__logo_files[fileIndex].ticket = outcome.ticket;
    }
  }

  logoCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__logo_files) {
        if (file.id === id) {
          toRemove = index;
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__logo_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  getPublicUrlValue(): string {
    return this.entityValues.publicUrl;
  }

  setPublicUrlValue(value: string) {
    this.entityValues.publicUrl = value;
  }

  getIdERPValue(): string {
    return this.entityValues.idERP;
  }

  setIdERPValue(value: string) {
    this.entityValues.idERP = value;
  }

  getIdCRMValue(): string {
    return this.entityValues.idCRM;
  }

  setIdCRMValue(value: string) {
    this.entityValues.idCRM = value;
  }

  getMonthlyTokenLimitValue(): number | undefined {
    return this.entityValues.monthlyTokenLimit;
  }

  setMonthlyTokenLimitValue(value: number | undefined) {
    this.entityValues.monthlyTokenLimit = value;
  }

  getCurrentMonthTokensValue(): number {
    return this.entityValues.currentMonthTokens;
  }

  setCurrentMonthTokensValue(value: number) {
    this.entityValues.currentMonthTokens = value;
  }

  getTokenLimitResetDayValue(): number {
    return this.entityValues.tokenLimitResetDay;
  }

  setTokenLimitResetDayValue(value: number) {
    this.entityValues.tokenLimitResetDay = value;
  }

  getLastTokenResetDateValue(): Date | null {
    return this.entityValues.lastTokenResetDate;
  }

  setLastTokenResetDateValue(value: Date | null) {
    this.entityValues.lastTokenResetDate = value;
  }

  setRagLlmProviderOptions(values: LlmProviderValuesType[]) {
    this.ragLlmProviderOptions = values;
  }

  *loadRagLlmProviderValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("LlmProvider");
    const allOptions = cache.getOptions(
      "LlmProvider"
    ) as LlmProviderValuesType[];

    const searchValue = this.ragLlmProviderSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setRagLlmProviderOptions(filtered);
  }

  getRagLlmProviderOptions() {
    return this.ragLlmProviderOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setRagLlmProviderValue(value: string) {
    this.ragLlmProviderSearchValue = value;
    yield this.loadRagLlmProviderValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedRagLlmProvider(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewLlmProvider(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "ragLlmProvider"
      );
    } else {
      if (value) {
        this.entityValues.showRagLlmProvider = value;
        this.entityValues.__ragLlmProvider = value.displayId;
      }
    }
  }

  getRagLlmModelValue(): string {
    return this.entityValues.ragLlmModel;
  }

  setRagLlmModelValue(value: string) {
    this.entityValues.ragLlmModel = value;
  }

  getCurrentStorageBytesValue(): number {
    return this.entityValues.currentStorageBytes;
  }

  setCurrentStorageBytesValue(value: number) {
    this.entityValues.currentStorageBytes = value;
  }

  getStorageByteLimitValue(): number | undefined {
    return this.entityValues.storageByteLimit;
  }

  setStorageByteLimitValue(value: number | undefined) {
    this.entityValues.storageByteLimit = value;
  }

  getESignatureProviderValue(): EsignatureProvider | "" {
    return this.entityValues.eSignatureProvider;
  }

  setESignatureProviderValue(value: EsignatureProvider | "") {
    this.entityValues.eSignatureProvider = value;
  }

  getESignatureProviderOptions() {
    return this.langStore.EsignatureProviderOptions;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as TenantErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as TenantErrorsType;
  }

  *fileDelete(ticket: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    yield api.deleteUploadedFile({ ticket });
  }

  addNewTenantType(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Tenant",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/tenantType/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewTenantStatus(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Tenant",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/tenantStatus/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewTenantPlan(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Tenant",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/tenantPlan/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewLlmProvider(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Tenant",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/llmProvider/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = TenantValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      tenant: { open: true, errors: [] },
      llmLimits: { open: true, errors: [] },
      storageLimits: { open: true, errors: [] },
    };
    this.tenantTypeSearchValue = "";
    this.tenantTypeOptions = [];
    this.statusSearchValue = "";
    this.statusOptions = [];
    this.planSearchValue = "";
    this.planOptions = [];
    this.ragLlmProviderSearchValue = "";
    this.ragLlmProviderOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: TenantValuesType): TenantErrorsType {
    const errors: TenantErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantNameLabel,
      };
      this.setCardError("tenant", errors.name.message);
    }
    {
      const pendingUpload = values.__logo_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.logo = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.TenantLogoLabel,
        };
        this.setCardError("tenant", errors.logo.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "TenantCreateStore"
    );
  }

  *duplicatesTenant(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const tenantInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (tenantInstance) {
      this.entityValues = getTenantValues(tenantInstance, this.langStore, true);
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(`/tenant/update/${returnData.resultId}`);
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewTenant(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewTenant(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      publicUrl: this.entityValues.publicUrl,
      idERP: this.entityValues.idERP,
      idCRM: this.entityValues.idCRM,
      monthlyTokenLimit: this.entityValues.monthlyTokenLimit,
      currentMonthTokens: this.entityValues.currentMonthTokens,
      tokenLimitResetDay: this.entityValues.tokenLimitResetDay,
      lastTokenResetDate: this.entityValues.lastTokenResetDate,
      ragLlmModel: this.entityValues.ragLlmModel,
      currentStorageBytes: this.entityValues.currentStorageBytes,
      storageByteLimit: this.entityValues.storageByteLimit,
      eSignatureProvider: this.entityValues.eSignatureProvider,
      tenantType: this.entityValues.showTenantType?.id || null,
      status: this.entityValues.showStatus?.id || null,
      plan: this.entityValues.showPlan?.id || null,
      ragLlmProvider: this.entityValues.showRagLlmProvider?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getTenantValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = TenantValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getTenantCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.tenantCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.tenantCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesTenant(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.name = initialDisplayId;
      }

      yield this.loadTenantTypeValues();
      yield this.loadStatusValues();
      yield this.loadPlanValues();
      yield this.loadRagLlmProviderValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
